import { useCallback, useMemo, useState } from "react";

import {
  ComplianceService,
  type FrameworkCode,
  type SubscriptionCreateBody,
  type SubscriptionResponse,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  KNOWN_FRAMEWORKS,
  formatFrameworkCode,
} from "@/lib/compliance-helpers";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Framework codes already subscribed — disabled in the picker so
   *  the user can't try to subscribe twice. */
  excludeCodes: ReadonlyArray<string>;
}

export function ComplianceSubscribeDialog({
  open,
  onClose,
  onSuccess,
  excludeCodes,
}: Props) {
  const { t } = useTranslation();
  const [framework, setFramework] = useState<FrameworkCode | "">("");
  const [targetDate, setTargetDate] = useState("");

  const available = useMemo(
    () => KNOWN_FRAMEWORKS.filter((c) => !excludeCodes.includes(c)),
    [excludeCodes]
  );

  const mutator = useCallback(
    (body: SubscriptionCreateBody) => ComplianceService.subscribe(body),
    []
  );

  const { mutate, isLoading, error, reset } = useMutation<
    SubscriptionCreateBody,
    SubscriptionResponse
  >(mutator, {
    onSuccess: () => {
      setFramework("");
      setTargetDate("");
      onSuccess();
    },
  });

  const handleClose = () => {
    reset();
    setFramework("");
    setTargetDate("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!framework) return;
    const body: SubscriptionCreateBody = { framework_code: framework };
    if (targetDate) body.target_certification_date = targetDate;
    await mutate(body);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("compliance.subscribeDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("compliance.subscribeDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div className="space-y-1.5">
            <label
              htmlFor="framework-picker"
              className="block text-sm font-medium text-ocean-900"
            >
              {t("compliance.subscribeDialog.frameworkLabel")}
            </label>
            <select
              id="framework-picker"
              value={framework}
              onChange={(e) => setFramework(e.target.value as FrameworkCode)}
              required
              className={cn(
                "h-10 w-full rounded-md border border-ocean-100 bg-white px-3 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-ocean-400"
              )}
            >
              <option value="">
                {t("compliance.subscribeDialog.frameworkPlaceholder")}
              </option>
              {available.map((code) => (
                <option key={code} value={code}>
                  {formatFrameworkCode(code)}
                </option>
              ))}
            </select>
            {available.length === 0 && (
              <p className="text-xs text-neutral-500">
                {t("compliance.subscribeDialog.allSubscribed")}
              </p>
            )}
          </div>

          <FormField
            label={t("compliance.subscribeDialog.targetDateLabel")}
            type="date"
            value={targetDate}
            onChange={setTargetDate}
            hint={t("compliance.subscribeDialog.targetDateHint")}
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("compliance.subscribeDialog.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !framework || available.length === 0}
          >
            {isLoading
              ? t("compliance.subscribeDialog.submitLoading")
              : t("compliance.subscribeDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
